import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, api } from '../../api/client';
import {
  Search, X, Plus, Loader2, ChevronLeft, ChevronRight, Save, Upload,
  Users, UserRound, Truck, ShieldAlert,
} from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { unwrapArray } from '../../lib/api-envelope';
import { fmtCurrency, parseMoney } from '../../lib/display-labels';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { formatCalendarDate, daysUntilCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { useAuth } from '../../contexts/AuthContext';
import { haulingEyebrow } from '../../lib/hauling';
import type { ApiResponse } from '../../types/api-manifest';
import type { TruckingDriverCreateResponse } from '../../types/trucking-roster';

export interface DriverCert {
  name: string;
  expiryDate: string | null;
  status: 'valid' | 'expiring' | 'expired' | 'unknown';
}
/**
 * THE STATUS WAS A LITERAL, AND THE KPI BUILT ON IT COULD NEVER FIRE.
 *
 * Both certs were mapped `status: 'valid' as const` from nothing but the
 * presence of a boolean flag — so a green **Valid** pill appeared beside an
 * expiry date that was blank, or years past. Worse, `certsExpiring` counts
 * `c.status !== 'valid'`, and with every cert hard-coded valid that KPI was
 * STRUCTURALLY ZERO: a safety figure on a dispatch screen that no data could
 * ever move. A tile that can only display one value is not a measurement.
 *
 * Derived from the expiry date now, with 'unknown' for a flag set against no
 * date. 'unknown' is deliberately NOT counted as expiring — a driver whose
 * expiry nobody recorded is not evidence of a lapse, and folding them into
 * the alarm would make the tile cry wolf across the fleet. The badge says
 * which it is, and that is where the gap belongs.
 *
 * Calendar days, parsed locally: an expiry is a date, not an instant, and
 * `new Date('2026-03-23')` is UTC midnight. Days left are counted from the
 * TENANT's today (companies.time_zone), not the device's.
 */
function certStatusFromExpiry(expiry: string | null, timeZone: string | null): DriverCert['status'] {
  const daysLeft = daysUntilCalendarDate(expiry, timeZone);
  if (daysLeft == null) return 'unknown';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 60) return 'expiring';
  return 'valid';
}

interface WizardForm {
  // Step 1: Basic info
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  licenseClass: string;
  licenseExpiry: string;
  // Step 2: Pay
  pay_method: 'percentage_of_bol' | 'flat_hourly';
  regular_rate: string;
  overtime_rate: string;
  shop_rate: string;
  standby_rate: string;
  per_diem_amount: string;
  pay_currency: 'CAD' | 'USD';
}

const WIZARD_INITIAL: WizardForm = {
  firstName: '', lastName: '', phone: '', email: '',
  licenseClass: 'Class 1', licenseExpiry: '',
  pay_method: 'percentage_of_bol',
  regular_rate: '', overtime_rate: '', shop_rate: '',
  standby_rate: '', per_diem_amount: '',
  pay_currency: 'CAD',
};

function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits.length ? `(${digits}` : '';
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function AddDriverWizard({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<WizardForm>({ ...WIZARD_INITIAL });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const update = (key: keyof WizardForm, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const isFlat = form.pay_method === 'flat_hourly';

  const handleOvertimeBlur = () => {
    if (isFlat && form.regular_rate && !form.overtime_rate) {
      update('overtime_rate', String(Math.round(parseFloat(form.regular_rate) * 1.5 * 100) / 100));
    }
  };

  const canAdvance = step === 1
    ? !!(form.firstName.trim() && form.lastName.trim())
    : true;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      // POST /trucking/drivers reads camelCase only (#815): `firstName` /
      // `lastName` are required, the pay columns are `regularPayRate`,
      // `overtimePayRate`, `shopPayRate`, `standbyPayRate`, `perDiemAmount`,
      // and the currency column is `currency`. The snake_case body was a 400
      // on every save. A blank field is left out, never "" (an empty expiry
      // fails the date cast).
      const num = (v: string) => (v.trim() === '' ? undefined : Number(v));
      const body: Record<string, unknown> = {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        licenseClass: form.licenseClass,
        payMethod: form.pay_method,
        currency: form.pay_currency,
      };
      if (form.phone.trim()) body.phone = form.phone.trim();
      if (form.email.trim()) body.email = form.email.trim();
      if (form.licenseExpiry) body.licenseExpiry = form.licenseExpiry;
      for (const [key, text] of [
        ['regularPayRate', form.regular_rate], ['overtimePayRate', form.overtime_rate], ['shopPayRate', form.shop_rate],
        ['standbyPayRate', form.standby_rate], ['perDiemAmount', form.per_diem_amount],
      ] as const) {
        const n = num(text);
        if (n === undefined) continue;
        if (!Number.isFinite(n) || n < 0) {
          setError('Pay rates must be numbers, 0 or more.');
          setSaving(false);
          return;
        }
        body[key] = n;
      }
      await apiClient.post<TruckingDriverCreateResponse>('/trucking/drivers', body);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create driver');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-fieldiq-text text-sm placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg shadow-2xl animate-fade-in max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border shrink-0">
          <div>
            <h3 className="text-lg font-bold text-fieldiq-text">Add Driver</h3>
            <p className="text-xs text-fieldiq-muted mt-0.5">Step {step} of 2</p>
          </div>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-fieldiq-text transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex gap-2 px-5 pt-3">
          {[1, 2].map(s => (
            <div key={s} className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-fieldiq-accent' : 'bg-fieldiq-border'}`} />
          ))}
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {error && (
            <div className="border border-fieldiq-danger/30 bg-fieldiq-danger/10 text-fieldiq-text px-3 py-2 rounded-lg text-sm mb-4">{error}</div>
          )}

          {/* STEP 1: Basic Info */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-fieldiq-text">Driver information</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-fieldiq-text mb-1">First Name *</label>
                  <input className={inputCls} value={form.firstName} onChange={e => update('firstName', e.target.value)} placeholder="John" />
                </div>
                <div>
                  <label className="block text-xs text-fieldiq-text mb-1">Last Name *</label>
                  <input className={inputCls} value={form.lastName} onChange={e => update('lastName', e.target.value)} placeholder="Smith" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-fieldiq-text mb-1">Phone</label>
                <input className={inputCls} value={form.phone} onChange={e => update('phone', formatPhoneInput(e.target.value))} placeholder="(780) 555-0000" maxLength={14} />
              </div>
              <div>
                <label className="block text-xs text-fieldiq-text mb-1">Email</label>
                <input type="email" className={inputCls} value={form.email} onChange={e => update('email', e.target.value)} placeholder="driver@company.com" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-fieldiq-text mb-1">Licence Class</label>
                  <select className={inputCls} value={form.licenseClass} onChange={e => update('licenseClass', e.target.value)}>
                    <option value="Class 1">Class 1</option>
                    <option value="Class 2">Class 2</option>
                    <option value="Class 3">Class 3</option>
                    <option value="Class 5">Class 5</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-fieldiq-text mb-1">Licence Expiry</label>
                  <input type="date" className={inputCls} value={form.licenseExpiry} onChange={e => update('licenseExpiry', e.target.value)} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Pay Method & Rates */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm font-semibold text-fieldiq-text">Driver pay method & rates</p>

              {/* Currency toggle */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-fieldiq-text">Currency:</span>
                <div className="flex bg-fieldiq-dark border border-fieldiq-border rounded-lg overflow-hidden">
                  {(['CAD', 'USD'] as const).map(c => (
                    <button
                      key={c}
                      onClick={() => update('pay_currency', c)}
                      className={`px-3 py-1.5 text-xs font-semibold transition-colors ${form.pay_currency === c ? 'bg-fi-amber text-fi-amber-ink' : 'text-fieldiq-muted hover:text-fieldiq-text'}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pay method selector */}
              <div className="flex gap-2">
                {([
                  { value: 'percentage_of_bol' as const, label: 'Percentage of BOL' },
                  { value: 'flat_hourly' as const, label: 'Flat Hourly Rate' },
                ] as const).map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => update('pay_method', opt.value)}
                    className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                      form.pay_method === opt.value
                        ? 'border-fi-amber bg-fi-amber/10 text-fieldiq-text'
                        : 'border-fieldiq-border text-fieldiq-text hover:border-fieldiq-muted'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${form.pay_method === opt.value ? 'border-fieldiq-accent' : 'border-fieldiq-border'}`}>
                      {form.pay_method === opt.value && <span className="w-2 h-2 rounded-full bg-fieldiq-accent" />}
                    </span>
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Rate inputs */}
              <div className="space-y-3">
                {[
                  { key: 'regular_rate' as const, label: 'Regular (Driving)', suffix: isFlat ? '/hr' : '%', prefix: isFlat ? '$' : '' },
                  { key: 'overtime_rate' as const, label: 'Overtime', suffix: isFlat ? '/hr' : '%', prefix: isFlat ? '$' : '', onBlur: handleOvertimeBlur },
                  { key: 'shop_rate' as const, label: 'Shop Pay', suffix: isFlat ? '/hr' : '%', prefix: isFlat ? '$' : '' },
                  { key: 'standby_rate' as const, label: 'Standby', suffix: isFlat ? '/hr' : '%', prefix: isFlat ? '$' : '' },
                  { key: 'per_diem_amount' as const, label: 'Per Diem', suffix: '/day', prefix: '$' },
                ].map(({ key, label, suffix, prefix, onBlur }) => (
                  <div key={key} className="flex items-center gap-3">
                    <span className="w-32 text-sm text-fieldiq-text shrink-0">{label}</span>
                    <div className="flex-1 relative">
                      {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-fieldiq-muted">{prefix}</span>}
                      <input
                        type="number"
                        step="0.01"
                        className={`${inputCls} ${prefix ? 'pl-6' : ''} pr-10`}
                        value={form[key]}
                        onChange={e => update(key, e.target.value)}
                        onBlur={onBlur}
                        placeholder="—"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-fieldiq-muted">{suffix}</span>
                    </div>
                  </div>
                ))}
              </div>

              {isFlat && form.regular_rate && !form.overtime_rate && (
                <p className="text-xs text-fieldiq-muted">Overtime will auto-fill to 1.5x regular rate when you leave the field</p>
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-5 py-4 border-t border-fieldiq-border shrink-0">
          {step > 1 ? (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 px-4 py-2.5 text-sm text-fieldiq-muted hover:text-fieldiq-text transition-colors">
              <ChevronLeft size={14} /> Back
            </button>
          ) : (
            <button onClick={onClose} className="px-4 py-2.5 text-sm text-fieldiq-muted hover:text-fieldiq-text transition-colors">Cancel</button>
          )}
          <div className="flex-1" />
          {step < 2 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance}
              className="flex items-center gap-1 px-5 py-2.5 bg-fi-amber text-fi-amber-ink rounded-xl text-sm font-semibold hover:brightness-105 transition-colors disabled:opacity-50"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-fi-amber text-fi-amber-ink rounded-xl text-sm font-semibold hover:brightness-105 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Driver'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Edit Pay Rates Modal (dynamic from positions) ──────────────── */
interface PayRateRow {
  position_id: string | null;
  pay_type: string;
  label: string;
  pct: string;
  day_rate: string;
  hourly_rate: string;
  pctDisabled?: boolean;
  dayDisabled?: boolean;
  hrDisabled?: boolean;
}

export function EditPayRatesModal({ driverId, onClose, onSuccess }: {
  driverId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [rows, setRows] = useState<PayRateRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const { data: positions = [] } = useQuery({
    queryKey: ['trucking-positions-all'],
    queryFn: () => apiClient.get('/trucking/positions/all').then(r => r.data?.data ?? r.data ?? []),
  });

  const { data: existingRates = [] } = useQuery({
    queryKey: ['trucking-driver-pay-rates', driverId],
    queryFn: () => api.trucking.drivers.getPayRates(driverId).then(r => r.data?.data ?? r.data ?? []),
    enabled: !!driverId,
  });

  // Build rows when positions + existing rates are loaded
  React.useEffect(() => {
    if (!positions) return;
    const activePositions = positions.filter((p: any) => p.is_active);

    const fixedRows: Omit<PayRateRow, 'pct' | 'day_rate' | 'hourly_rate'>[] = [
      { position_id: null, pay_type: 'standby', label: 'Standby' },
      { position_id: null, pay_type: 'shop', label: 'Shop Pay' },
      { position_id: null, pay_type: 'per_diem', label: 'Per Diem', pctDisabled: true, hrDisabled: true },
      { position_id: null, pay_type: 'overtime', label: 'Overtime', dayDisabled: true },
    ];

    const allRows: PayRateRow[] = [
      ...activePositions.map((p: any) => {
        const existing = existingRates.find((r: any) => r.position_id === p.id && r.pay_type === 'position');
        return {
          position_id: p.id,
          pay_type: 'position',
          label: p.name,
          pct: existing?.pct != null ? String(existing.pct) : '',
          day_rate: existing?.day_rate != null ? String(existing.day_rate) : '',
          hourly_rate: existing?.hourly_rate != null ? String(existing.hourly_rate) : '',
        };
      }),
      ...fixedRows.map(fr => {
        const existing = existingRates.find((r: any) => r.pay_type === fr.pay_type && !r.position_id);
        return {
          ...fr,
          pct: existing?.pct != null ? String(existing.pct) : '',
          day_rate: existing?.day_rate != null ? String(existing.day_rate) : '',
          hourly_rate: existing?.hourly_rate != null ? String(existing.hourly_rate) : '',
        };
      }),
    ];

    setRows(allRows);
    setLoading(false);
  }, [positions, existingRates]);

  const updateRow = (idx: number, field: 'pct' | 'day_rate' | 'hourly_rate', value: string) => {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
  };


  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const rates = rows
        .filter(r => r.pct || r.day_rate || r.hourly_rate)
        .map(r => ({
          position_id: r.position_id || null,
          pay_type: r.pay_type,
          pct: r.pct ? parseFloat(r.pct) : null,
          day_rate: r.day_rate ? parseFloat(r.day_rate) : null,
          hourly_rate: r.hourly_rate ? parseFloat(r.hourly_rate) : null,
        }));
      await api.trucking.drivers.savePayRates(driverId, rates);
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save pay rates');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full text-xs bg-fieldiq-dark border border-fieldiq-border rounded-lg px-1.5 py-1.5 text-fieldiq-text text-center outline-none focus:border-fieldiq-accent';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-fieldiq-border shrink-0">
          <h3 className="text-lg font-bold text-fieldiq-text">Edit Pay Rates</h3>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-fieldiq-text transition-colors">
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {error && (
            <div className="border border-fieldiq-danger/30 bg-fieldiq-danger/10 text-fieldiq-text px-3 py-2 rounded-lg text-sm mb-4">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-fieldiq-accent" />
            </div>
          ) : (
            <>
              {/* Column headers */}
              <div className="grid grid-cols-[1fr_65px_75px_65px] gap-1.5 mb-2">
                <span className="text-xs text-fieldiq-muted font-semibold">Line Item</span>
                <span className="text-xs text-fieldiq-muted font-semibold text-center">%</span>
                <span className="text-xs text-fieldiq-muted font-semibold text-center">Day $</span>
                <span className="text-xs text-fieldiq-muted font-semibold text-center">Hr $</span>
              </div>

              <div className="space-y-1.5">
                {rows.map((row, idx) => (
                  <div key={`${row.pay_type}-${row.position_id ?? 'fixed'}`} className="grid grid-cols-[1fr_65px_75px_65px] gap-1.5 items-center">
                    <span className="text-sm text-fieldiq-text truncate">{row.label}</span>
                    {row.pctDisabled ? (
                      <span className="text-xs text-fieldiq-muted text-center">—</span>
                    ) : (
                      <input type="number" step="0.5" value={row.pct} onChange={e => updateRow(idx, 'pct', e.target.value)} placeholder="—" className={inputCls} />
                    )}
                    {row.dayDisabled ? (
                      <span className="text-xs text-fieldiq-muted text-center">—</span>
                    ) : (
                      <input type="number" step="0.01" value={row.day_rate} onChange={e => updateRow(idx, 'day_rate', e.target.value)} placeholder="—" className={inputCls} />
                    )}
                    {row.hrDisabled ? (
                      <span className="text-xs text-fieldiq-muted text-center">—</span>
                    ) : (
                      <input type="number" step="0.01" value={row.hourly_rate} onChange={e => updateRow(idx, 'hourly_rate', e.target.value)} placeholder="—" className={inputCls} />
                    )}
                  </div>
                ))}
              </div>

              <p className="text-xs text-fieldiq-muted mt-3">
                Enter percentages as whole numbers (e.g. 25 for 25%). Leave blank for N/A.
              </p>
            </>
          )}
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-fieldiq-border shrink-0">
          <button onClick={onClose} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-fieldiq-text py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || loading} className="flex-1 bg-fi-amber text-fi-amber-ink py-2.5 rounded-xl text-sm font-semibold hover:brightness-105 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Saving...' : 'Save Pay Rates'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── The register ───────────────────────────────────────────────── */

/**
 * A driver as GET /trucking/drivers sends it: `d.*` (trucking_drivers) plus
 * `assigned_truck: { id, unit_number } | null` from `trucking_fleet`.
 *
 * WHAT THIS PAGE USED TO SHOW THAT NOTHING SENDS, AND IS GONE:
 * - The Hours of Service recap: "Driving today", "On duty today", "Cycle
 *   hours", "Off duty streak", all hard-coded 0, under bars against 13 / 14 /
 *   70 / 10h, plus the HOS Warnings tile counting them. The API's HOS summary
 *   (GET /drivers/hos) is a rolling 7-DAY total, not today's, so it cannot
 *   fill "today" bars. It now sends null for a driver with no logs (#815), so a
 *   7-day readout here would be honest; that is a separate change.
 * - "This week": hours and earnings, hard-coded 0.
 * - "+ $0.00/km": there is no km-rate column.
 * - Recent Loads This Week, always empty and saying "No loads this week".
 * - Truck type, cycle reset date and cert issue dates, which have no column.
 * - Every driver read as "On shift" (status `active` was mapped to it), and the
 *   "On shift today" tile counted every active driver.
 */
export interface DriverRow {
  id: string;
  name: string;
  status: string | null;
  truck: string | null;
  /** The assigned `trucking_fleet` unit's id, for a link to its page. */
  truck_id: string | null;
  phone: string | null;
  email: string | null;
  licence_class: string | null;
  licence_number: string | null;
  licence_expiry: string | null;
  /** From `licence_expiry` alone, the same rule as the certs: no date is 'unknown', never valid. */
  licence_status: DriverCert['status'];
  hire_date: string | null;
  pay_method: string | null;
  regular_pay_rate: number | null;
  currency: string | null;
  /** The login linked to this driver record, or null. What the field app's "my driver" lookup tries first. */
  user_id: string | null;
  certs: DriverCert[];
}

export const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));

export function toDriver(d: Record<string, unknown>, timeZone: string | null): DriverRow {
  const truck = d.assigned_truck as { id?: unknown; unit_number?: unknown } | null | undefined;
  const certs: DriverCert[] = [];
  // A cert is listed when its flag is set. Its status comes from the expiry
  // date alone; a flag with no date is "No expiry on file", never Valid.
  if (d.air_brake_cert === true) certs.push({ name: 'Air Brake', expiryDate: str(d.air_brake_expiry), status: certStatusFromExpiry(str(d.air_brake_expiry), timeZone) });
  if (d.h2s_alive === true) certs.push({ name: 'H2S Alive', expiryDate: str(d.h2s_expiry), status: certStatusFromExpiry(str(d.h2s_expiry), timeZone) });
  return {
    id: String(d.id),
    name: [str(d.first_name), str(d.last_name)].filter(Boolean).join(' ') || '—',
    status: str(d.status),
    truck: str(truck?.unit_number),
    truck_id: str(truck?.id),
    phone: str(d.phone),
    email: str(d.email),
    licence_class: str(d.license_class),
    licence_number: str(d.license_number),
    licence_expiry: str(d.license_expiry),
    licence_status: certStatusFromExpiry(str(d.license_expiry), timeZone),
    hire_date: str(d.hire_date),
    pay_method: str(d.pay_method),
    regular_pay_rate: parseMoney(d.regular_pay_rate),
    // The column is `currency`. This page read `pay_currency`, which is not
    // sent, and so showed CAD for every driver whatever was stored.
    currency: str(d.currency),
    user_id: str(d.user_id),
    certs,
  };
}

type Skin = { label: string; fill: string; rail: string };
/**
 * `trucking_drivers.status` is free text; the API writes `active` (default),
 * and `retired` (DELETE). `inactive` is accepted on import. Anything else is a
 * slate dash with no rail.
 */
const STATUS: Record<string, Skin> = {
  active: { label: 'Active', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  inactive: { label: 'Inactive', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  retired: { label: 'Retired', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
const UNKNOWN: Skin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
export const skinOf = (s: string | null) => (s != null && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : UNKNOWN);

export const CERT_BADGE: Record<DriverCert['status'], { label: string; fill: string }> = {
  valid: { label: 'Valid', fill: 'bg-fi-signal-green' },
  expiring: { label: 'Renew soon', fill: 'bg-fi-signal-amber' },
  expired: { label: 'Expired', fill: 'bg-fi-signal-red' },
  unknown: { label: 'No expiry on file', fill: 'bg-fi-signal-slate' },
};

/** Exhaustive over the two pay methods the wizard and the pay engine know. */
export const PAY_METHOD: Record<string, string> = {
  percentage_of_bol: 'Percentage of BOL',
  flat_hourly: 'Flat hourly',
};

export const badgeCls = 'inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink';

export function DriverStatusBadge({ status }: { status: string | null }) {
  const skin = skinOf(status);
  return <span className={`${badgeCls} ${skin.fill}`}>{skin.label}</span>;
}

/**
 * AN EXPIRED LICENCE SAYS "EXPIRED", IN RED (fix/trucking-figures-agree).
 * Mike Kowalski's licence lapsed on 2026-08-31 and this register printed
 * "Exp Aug 31, 2026" in muted ink beside an Active badge, while Dispatch
 * called it "Renewal due". The badge is CERT_BADGE's: red Expired, amber
 * Renew soon. A current licence, or one with no date, draws no badge: the
 * date beside it already says which.
 */
export function LicenceBadge({ status }: { status: DriverCert['status'] }) {
  if (status !== 'expired' && status !== 'expiring') return null;
  const skin = CERT_BADGE[status];
  return (
    <span data-testid="licence-badge" className={`${badgeCls} ${skin.fill}`}>
      {skin.label}
    </span>
  );
}

/** The certs cell: expired ones in red, then ones to renew in amber, else how many are on file. */
export function CertsCell({ certs }: { certs: DriverCert[] }) {
  if (certs.length === 0) return <>None on file</>;
  const expired = certs.filter((c) => c.status === 'expired').length;
  const expiring = certs.filter((c) => c.status === 'expiring').length;
  if (expired > 0) return <span data-testid="certs-expired" className={`${badgeCls} ${CERT_BADGE.expired.fill}`}>{expired} expired</span>;
  if (expiring > 0) return <span className={`${badgeCls} ${CERT_BADGE.expiring.fill}`}>{expiring} renew soon</span>;
  return <>{certs.length} on file</>;
}

export const fmtDay = (d: string | null) => formatCalendarDate(d, { year: 'numeric', month: 'short', day: 'numeric' }) ?? '—';
export const isLapsing = (c: DriverCert) => c.status === 'expiring' || c.status === 'expired';

/**
 * `regular_pay_rate` is the HOURLY rate the pay engine uses for a flat-hourly
 * driver (`trucking-pay.service.ts`). A percentage-of-BOL driver is paid from
 * their pay-rate rows instead, so an hourly figure would be the wrong fact.
 */
export function payRateText(d: DriverRow): string {
  if (d.pay_method === 'flat_hourly') return d.regular_pay_rate == null ? '—' : `${fmtCurrency(d.regular_pay_rate)} / hr`;
  if (d.pay_method === 'percentage_of_bol') return 'See pay rates';
  return '—';
}

export type TenantUser = { id: string; label: string };

/**
 * The driver's LOGIN and EMAIL (#815). The field app finds "my driver record"
 * by `trucking_drivers.user_id`, then by an exact email match, and nothing
 * set either from a screen: `user_id` had no UI at all and the email could
 * only be typed in the (broken) create wizard. Both save through
 * PUT /trucking/drivers/:id (`userId`, which the API checks belongs to this
 * company, and `email`).
 */
export function DriverAccountEditor({
  driver, users, usersError, onSave,
}: {
  driver: DriverRow;
  users: TenantUser[] | undefined;
  usersError: string | null;
  onSave: (body: { userId?: string | null; email?: string | null }, what: string) => Promise<void>;
}) {
  const [userId, setUserId] = useState(driver.user_id ?? '');
  const [email, setEmail] = useState(driver.email ?? '');
  const [busy, setBusy] = useState(false);
  const linked = driver.user_id ? (users ?? []).find((u) => u.id === driver.user_id) : null;
  const save = async (body: { userId?: string | null; email?: string | null }, what: string) => {
    setBusy(true);
    try { await onSave(body, what); } finally { setBusy(false); }
  };
  const selectCls = 'h-9 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none';
  return (
    // Drawn inside the driver page's Login panel, which carries the heading.
    <div data-testid="driver-account">
      <p className="mb-2 text-sm text-fieldiq-text">
        {driver.user_id
          ? (linked ? `Linked to ${linked.label}` : 'Linked to a login this list could not name')
          : 'Not linked'}
      </p>
      {usersError ? (
        <p className="text-xs text-fieldiq-text">{usersError}</p>
      ) : (
        <div className="flex gap-2">
          <label htmlFor={`driver-login-${driver.id}`} className="sr-only">Login for this driver</label>
          <select id={`driver-login-${driver.id}`} value={userId} onChange={(e) => setUserId(e.target.value)} className={selectCls} disabled={!users}>
            <option value="">{users ? 'Not linked' : 'Loading logins…'}</option>
            {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
          </select>
          <button
            type="button"
            disabled={busy || userId === (driver.user_id ?? '')}
            onClick={() => save({ userId: userId || null }, userId ? 'Login linked' : 'Login unlinked')}
            className="fi-action fi-action--standard shrink-0 whitespace-nowrap border border-fieldiq-border disabled:opacity-50"
          >
            Save login
          </button>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <label htmlFor={`driver-email-${driver.id}`} className="sr-only">Driver email</label>
        <input
          id={`driver-email-${driver.id}`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email on the driver record"
          className={selectCls}
        />
        <button
          type="button"
          disabled={busy || email.trim() === (driver.email ?? '')}
          onClick={() => save({ email: email.trim() || null }, 'Email saved')}
          className="fi-action fi-action--standard shrink-0 whitespace-nowrap border border-fieldiq-border disabled:opacity-50"
        >
          Save email
        </button>
      </div>
    </div>
  );
}

export default function DriverManagementScreen() {
  const { hasVertical } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showWizard, setShowWizard] = useState(false);
  const [showRetired, setShowRetired] = useState(false);
  const timeZone = useTenantTimeZone();

  const driversQuery = useQuery({
    // The zone is in the key: each row's cert status is counted from the tenant's today.
    queryKey: ['trucking-drivers', showRetired, timeZone],
    queryFn: () =>
      apiClient
        .get<ApiResponse<'GET /api/v1/trucking/drivers'>>(`/trucking/drivers${showRetired ? '?include_retired=true' : ''}`)
        .then((r) => unwrapArray<Record<string, unknown>>(r.data).map((d) => toDriver(d, timeZone))),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failed = queryLoadFailure(driversQuery) != null;
  const loading = driversQuery.isLoading && !failed;
  const drivers = failed ? undefined : driversQuery.data;

  const filtered = useMemo(() => {
    const rows = drivers ?? [];
    if (!search) return rows;
    const s = search.toLowerCase();
    return rows.filter((d) => [d.name, d.truck, d.phone, d.email, d.licence_number].some((v) => (v ?? '').toLowerCase().includes(s)));
  }, [drivers, search]);

  // Band figures: tallies of the loaded list (the route is unpaginated). The
  // roster figures exclude retired drivers even while they are shown.
  const roster = (drivers ?? []).filter((d) => d.status !== 'retired');
  const tally = (n: number) => (failed ? '—' : displayValue(drivers ? n : null));

  // A row opens the driver's own page (#815: registers, not split panes).
  const open = (id: string) => navigate(`/trucking/drivers/${encodeURIComponent(id)}`);

  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="drivers-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={haulingEyebrow(hasVertical)}
        title="Drivers"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={() => navigate('/import/drivers')} className="fi-action fi-action--standard border border-white/25 hover:border-white/60">
              <Upload className="size-4" aria-hidden="true" />
              Import
            </button>
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className="fi-action fi-action--standard fi-action--primary"
            >
              <Plus className="size-4" aria-hidden="true" />
              Add Driver
            </button>
          </div>
        }
        stats={[
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Drivers', sub: 'Not retired', tone: 'neutral', loading, value: tally(roster.length) },
          { icon: <UserRound className="size-4" aria-hidden="true" />, label: 'Active', sub: 'Status active', tone: 'success', loading, value: tally(roster.filter((d) => d.status === 'active').length) },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'No Truck', sub: 'Unassigned', tone: 'info', loading, value: tally(roster.filter((d) => d.truck == null).length) },
          { icon: <ShieldAlert className="size-4" aria-hidden="true" />, label: 'Certs Due', sub: 'Lapsed or soon', tone: 'accent', loading, value: tally(roster.reduce((n, d) => n + d.certs.filter(isLapsing).length, 0)) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">

          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="flex flex-1 gap-2">
              <button
                type="button"
                aria-pressed={showRetired}
                onClick={() => setShowRetired(!showRetired)}
                className={`inline-flex items-center whitespace-nowrap rounded-fi-pill border px-4 py-1.5 text-xs font-extrabold transition ${
                  showRetired ? 'border-fieldiq-text bg-fieldiq-card text-fieldiq-text' : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                Show retired
              </button>
            </div>
            <div className="relative lg:w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
              <input
                placeholder="Search name, truck, phone..."
                aria-label="Search drivers"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
              />
            </div>
          </div>

          <section
            aria-labelledby="driver-register-title"
            className="min-w-0 overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Driver register</p>
              <h2 id="driver-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Drivers</h2>
            </header>
            {failed ? (
              <LoadFailureNotice error={queryFailureError(driversQuery)} subject="drivers" />
            ) : loading ? (
              <div className="space-y-2 p-4" aria-hidden="true">
                {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />)}
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                {(drivers ?? []).length === 0 ? 'No drivers yet' : 'No drivers match this search'}
              </p>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[760px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">Driver</th>
                        <th className="px-4 py-3">Truck</th>
                        <th className="px-4 py-3">Licence</th>
                        <th className="px-4 py-3">Phone</th>
                        <th className="px-4 py-3">Certs</th>
                        <th className="px-4 py-3">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((d) => {
                        return (
                          <tr
                            key={d.id}
                            data-testid="driver-row"
                            role="link"
                            tabIndex={0}
                            onClick={() => open(d.id)}
                            onKeyDown={rowKeys(d.id)}
                            className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${skinOf(d.status).rail}`}
                          >
                            <td className="px-4 py-3 text-sm font-extrabold text-fieldiq-text">{d.name}</td>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-text">{d.truck ?? '—'}</td>
                            <td className="px-4 py-3">
                              <p className="text-sm text-fieldiq-text">{d.licence_class ?? '—'}</p>
                              <p className="text-[11px] text-fieldiq-muted">Exp {fmtDay(d.licence_expiry)}</p>
                              <div className="mt-1"><LicenceBadge status={d.licence_status} /></div>
                            </td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">{d.phone ?? '—'}</td>
                            <td className="px-4 py-3 text-sm text-fieldiq-text">
                              <CertsCell certs={d.certs} />
                            </td>
                            <td className="px-4 py-3"><DriverStatusBadge status={d.status} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {filtered.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      data-testid="driver-card"
                      onClick={() => open(d.id)}
                      className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-extrabold text-fieldiq-text">{d.name}</p>
                          <p className="text-xs text-fieldiq-muted">{d.truck ?? 'No truck'} · {d.licence_class ?? '—'}</p>
                          <div className="mt-1"><LicenceBadge status={d.licence_status} /></div>
                        </div>
                        <DriverStatusBadge status={d.status} />
                      </div>
                    </button>
                  ))}
                </div>
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {filtered.length} of {(drivers ?? []).length} drivers{showRetired ? ', retired included' : ''}
                </p>
              </>
            )}
          </section>
        </div>
      </section>

      {showWizard && (
        <AddDriverWizard
          onClose={() => setShowWizard(false)}
          onSuccess={() => {
            setShowWizard(false);
            queryClient.invalidateQueries({ queryKey: ['trucking-drivers'] });
          }}
        />
      )}
    </div>
  );
}
